import React, {useEffect, useState} from 'react';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import InputMUI from '../../../shared/InputMUI';
import SelectComponent from '../../../shared/SelectComponent';
import {AutocompleteMUI} from '../../../shared';
import ButtonMUI from '../../../shared/ButtonMUI';
import CompaniesHero from '../../Components/Companies/CompaniesHero';
import {ReactComponent as RemoveIcon} from '../../../assets/icons/remove.svg';
import {ReactComponent as PlusIcon} from '../../../assets/icons/plus-icon.svg';
import './CompaniesEditing.scss';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import LoadPanel from '../../Components/LoadPanel';
import {getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import DropzoneAvatar from '../../Components/DropzoneAvatar';
import AvatarIcon from '../../../assets/images/companies/avatar.svg';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import {editCompany, getCompanies, getCompany, getCompanyAvatars} from '../../../redux/actions/company/companyActions';
import {notifySuccess} from '../../../helpers/notifySnack';
import {getCompanyEmployeeList, getEmployee} from '../../../redux/actions/employee/employeeActions';
import {hasPermission} from '../../../helpers/functions';
import {PERMISSIONS} from '../../../helpers/constants';
import {useTranslation} from 'react-i18next';

const CompaniesEditing = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const singleCompany = useSelector((state) => state.company.singleCompany);
    const avatars = useSelector((state) => state.company.avatars);
    const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
    const [filteredCities, setFilteredCities] = useState([]);
    const {employee, employeeLoading} = useSelector(({employee}) => employee);
    const {profileMe} = useSelector((state) => state.profile);

    const schema = yup.object().shape({
        name: yup.string().required(t('CompaniesEditing.name_required')),
        countryId: yup.string().required(t('CompaniesEditing.country_required')),
        city: yup.string().required(t('CompaniesEditing.city_required')),
        address: yup.string().required(t('CompaniesEditing.address_required')),
        website: yup
            .string()
            .trim()
            .nullable()
            .notRequired()
            .test('is-url', t('validations.site'), (value) => {
                if (!value) return true;
                return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/.test(value);
            }),
        email: yup.string().email(t('CompaniesEditing.email_invalid')).required(t('CompaniesEditing.email_required')),
        phones: yup.array().of(
            yup.object().shape({
                phone: yup.string().required(t('CompaniesEditing.phone_required'))
            })
        )
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: {errors, isValid, isSubmitting}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            phones: [{phone: ''}],
            name: '',
            countryId: '',
            city: '',
            address: '',
            email: '',
            employee: [],
            website: '',
            logo: null
        }
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'phones'
    });

    const onFilesAdded = (files) => {
        setValue('logo', files);
    };

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();

            formData.append('name', data.name);
            formData.append('countryId', data.countryId);
            formData.append('cityId', data.city);
            formData.append('address', data.address);
            formData.append('email', data.email);

            if (data.logo) {
                formData.append('logoId', data.logo);
            }

            data.phones.forEach((item, index) => {
                formData.append(`phones[${index}]`, item.phone);
            });
            if (data.website) {
                formData.append('site', data.website);
            }

            data.employee.forEach((employee, index) => {
                formData.append(`employees[${index}]`, employee.id);
            });

            const res = await dispatch(editCompany(formData, id));

            if (res.payload && res.payload.status === 200) {
                await dispatch(getCompanies());
                await dispatch(getCompany(id));
                await dispatch(getCompanyEmployeeList(id));
                notifySuccess(t('all.successfully'));
            }
        } catch (error) {
        }
    };

    const handleCountryChange = (selectedCountryId) => {
        const filtered = cities.filter((city) => city.countryId === selectedCountryId);
        setFilteredCities(filtered);
    };

    useEffect(() => {
        const countryId = watch('countryId');
        handleCountryChange(countryId);
    }, [cities, watch('countryId')]);

    useEffect(() => {
        const fetchFunc = async () => {
            if (countriesLoading) {
                await dispatch(getCountries());
            }
            if (citiesLoading) {
                await dispatch(getCities());
            }
            if (employeeLoading) {
                await dispatch(getEmployee());
            }
            if (avatars?.loading) {
                await dispatch(getCompanyAvatars());
            }
        };

        fetchFunc();
    }, [id, dispatch]);

    useEffect(() => {
        if (singleCompany && Object.keys(singleCompany).length) {
            reset({
                name: singleCompany.name || '',
                countryId: singleCompany.country ? singleCompany.country.id : '',
                city: singleCompany.city ? singleCompany.city.id : '',
                address: singleCompany.address || '',
                email: singleCompany.email || '',
                phones:
                    singleCompany.phones && singleCompany.phones.length > 0
                        ? singleCompany.phones.map((phone) => ({phone: phone}))
                        : [{phone: ''}],
                employee: singleCompany.employees
                    ? singleCompany.employees.map((item) => ({
                        label: item.firstName ?? '',
                        id: item.id,
                        avatar: item.avatar ?? null,
                        text: item.position,
                        isMe: profileMe?.id === item.id,
                    }))
                    : [],
                website: singleCompany.site || ''
            });
        }
    }, [singleCompany]);

    const logoWatch = watch('logo');

    const disabled = logoWatch && Object.keys(logoWatch).length === 0;

    const employeeArr = employee.map((item) => {
        return {
            label: item.firstName ?? '',
            id: item.id,
            avatar: item.avatar?.thumbnail ?? null,
            text: item.positionName,
            isMe: profileMe?.id === item.id
        };
    });

    const permissionEdit = hasPermission(PERMISSIONS.COMPANY_EDIT, profileMe?.permissions ?? []);
    return (
        <div className='companies-editing'>
            <div className='companies-editing-name'>{singleCompany?.name ? singleCompany?.name : ''}</div>
            {citiesLoading || countriesLoading || avatars?.loading ? (
                <LoadPanel noBg={true} circleSize={50} staticPos={true}/>
            ) : (
                <>
                    <CompaniesHero subHeading={t('CompaniesEditing.heading_edit')}/>

                    <div className='form-wrapper'>
                        <form className='form-editing' onSubmit={handleSubmit(onSubmit)}>
                            <div className='companies__dropzone'>
                                <DropzoneAvatar
                                    initialAvatarUrl={{
                                        avatar: singleCompany?.logo,
                                        id: singleCompany?.logoId,
                                    }}
                                    initialAvatar={AvatarIcon}
                                    onFileAdded={onFilesAdded}
                                    dropText={t('CompaniesEditing.drop_text')}
                                    path={POPUPS.AVATAR_POPUP}
                                    images={avatars?.list || []}
                                    disabled={!permissionEdit}
                                    needLoader={true}
                                />
                            </div>
                            <div className='companies__input'>
                                <Controller
                                    name='name'
                                    control={control}
                                    render={({field}) => (
                                        <InputMUI
                                            type='text'
                                            fullWidth
                                            className='no-icons'
                                            error={errors.name?.message}
                                            inputProps={field}
                                            label={t('CompaniesEditing.name_label')}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>

                            <div className='companies__input'>
                                <Controller
                                    name='countryId'
                                    control={control}
                                    render={({field}) => (
                                        <SelectComponent
                                            onChange={(e) => {
                                                field.onChange(e.target?.value ?? '');
                                                setValue('city', '', {shouldValidate: true});
                                            }}
                                            onBlur={field.onBlur}
                                            value={countries.find((c) => {
                                                return c.id === field.value;
                                            })}
                                            options={countries}
                                            error={errors.countryId?.message}
                                            label={t('CompaniesEditing.country_label')}
                                            placeholder=''
                                            inputProps={field}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>

                            <div className='companies__input'>
                                <Controller
                                    name='city'
                                    control={control}
                                    render={({field}) => (
                                        <SelectComponent
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            value={filteredCities.find((c) => c.id === field.value)}
                                            options={filteredCities}
                                            label={t('CompaniesEditing.city_label')}
                                            placeholder=''
                                            error={errors.city?.message}
                                            inputProps={field}
                                            disabled={!filteredCities.length || !permissionEdit}
                                        />
                                    )}
                                />
                            </div>

                            <div className='companies__input'>
                                <Controller
                                    name='address'
                                    control={control}
                                    render={({field}) => (
                                        <InputMUI
                                            type='text'
                                            fullWidth
                                            className='no-icons'
                                            error={errors.address?.message}
                                            inputProps={field}
                                            label={t('CompaniesEditing.address_label')}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>
                            <div className='companies__input'>
                                <Controller
                                    name='email'
                                    control={control}
                                    render={({field}) => (
                                        <InputMUI
                                            type='email'
                                            fullWidth
                                            className='no-icons'
                                            error={errors.email?.message}
                                            inputProps={field}
                                            label={t('CompaniesEditing.email_label')}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>

                            {fields.map((field, index) => {
                                return (
                                    <div key={field.id} className='companies__input'>
                                        <Controller
                                            name={`phones[${index}].phone`}
                                            control={control}
                                            render={({field}) => {
                                                return (
                                                    <div className={'companies__input-inner'}>
                                                        <InputMUI
                                                            disabled={!permissionEdit}
                                                            fullWidth
                                                            className={`no-icons ${index > 0 ? 'with-clear' : ''}`}
                                                            error={errors.phones?.[index]?.phone?.message}
                                                            onChange={(e) => {
                                                                const value = e.target.value;

                                                                let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                                                if (cleanValue.charAt(0) !== '+') {
                                                                    cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                                                } else {
                                                                    cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                                                }

                                                                if (/^\+?\d*$/.test(cleanValue)) {
                                                                    field.onChange(cleanValue);
                                                                }
                                                            }}
                                                            value={field.value}
                                                            type={'tel'}
                                                            label={`${
                                                                index > 0
                                                                    ? t('all.additional') + ' ' + t('CompaniesPopup.phoneL')
                                                                    : t('CompaniesPopup.phone')
                                                            }`}
                                                            // label={`${t('CompaniesEditing.phone_label')} ${index > 0 ? index + 1 : ''}`}
                                                            inputProps={field}
                                                        />

                                                        {index > 0 && permissionEdit && (
                                                            <div className='companies__trash'
                                                                 onClick={() => remove(index)}>
                                                                {<RemoveIcon/>}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>
                                );
                            })}

                            <div
                                className={`companies__dynamic${!permissionEdit ? ' disabled' : ''}`}
                                onClick={() => permissionEdit && append({phone: ''})}
                            >
                                <PlusIcon/>
                                <span>{t('CompaniesEditing.add_phone')}</span>
                            </div>
                            <div className='companies__input'>
                                <Controller
                                    name='website'
                                    control={control}
                                    render={({field}) => (
                                        <InputMUI
                                            type='text'
                                            fullWidth
                                            className='no-icons'
                                            error={errors.website?.message}
                                            inputProps={field}
                                            label={t('CompaniesEditing.website_label')}
                                            disabled={!permissionEdit}
                                        />
                                    )}
                                />
                            </div>
                            <div className='companies__select'>
                                <p>{t('CompaniesEditing.employee_label')}</p>
                                <Controller
                                    name='employee'
                                    control={control}
                                    render={({field}) => {
                                        return (
                                            <AutocompleteMUI
                                                className='no-icons'
                                                text={t('CompaniesEditing.select_from_list')}
                                                array={employeeArr}
                                                error={errors?.employee}
                                                value={field.value}
                                                onChange={field.onChange}
                                                disabled={!permissionEdit}
                                            />
                                        );
                                    }}
                                />
                            </div>
                            <div className='companies__submit'>
                                {permissionEdit && (
                                    <ButtonMUI
                                        disabled={!isValid || disabled || isSubmitting}
                                        loading={isSubmitting}
                                        formAction
                                        fullWidth={true}
                                    >
                                        {t('CompaniesEditing.save_button')}
                                    </ButtonMUI>
                                )}
                            </div>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
};

export default CompaniesEditing;
